// lib/koi-pond-v2/shaders/pondFloor.ts
//
// Procedural pond floor with caustics composited on top. Three layers:
//   1. Cellular noise (pebble shapes, larger features at ~8 cells/UV)
//   2. Voronoi-ish sand variation (finer mid-frequency tone shifts via
//      hash-driven offsets at ~200 cells/UV)
//   3. Caustic field (same domain-warped voronoi as caustic.ts, 12s
//      cycle) — composited last so cells sit on top of the floor.
//
// Camera angle is locked at 35° down so the floor recedes — same
// camera tilt the eventual pond scene uses.

import { ShaderMaterial, Vector2 } from 'three';
import { paletteVec3 } from '../palette';
import { SAFE_ZONE_COMPLEMENT } from '../constants/safeZone';

export const POND_FLOOR_VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const POND_FLOOR_FRAG = `
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform vec2 uResolution;

  vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453);
  }
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  // Voronoi minimum-distance with animated points. Period 12s on the
  // primary sin term — same math as caustic.ts so the composited caustic
  // layer here cycles in sync with the standalone caustic panel.
  float voronoi(vec2 uv, float t) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float minDist = 1.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 neighbor = vec2(float(x), float(y));
        vec2 point = hash2(i + neighbor);
        point = 0.5 + 0.5 * sin(t * 0.5236 + 6.2831 * point);
        vec2 diff = neighbor + point - f;
        minDist = min(minDist, length(diff));
      }
    }
    return minDist;
  }
  // Cellular (worley) noise — distance to nearest STATIC feature point.
  // No time dependence; the pebble pattern is fixed.
  float cellular(vec2 uv) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float minDist = 1.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 neighbor = vec2(float(x), float(y));
        vec2 point = hash2(i + neighbor);
        vec2 diff = neighbor + point - f;
        minDist = min(minDist, length(diff));
      }
    }
    return minDist;
  }

  void main() {
    // Layer 1: large pebble shapes — 8 cells across the UV
    float pebbles = cellular(vUv * 8.0);
    pebbles = smoothstep(0.0, 0.3, pebbles);

    // Layer 2: finer sand variation — hash at 200x quantization, low
    // amplitude so it reads as grit, not noise
    float sand = hash(floor(vUv * 200.0)) * 0.05;

    // Combined floor color — mid water tones, pebbles slightly darker
    vec3 base = ${paletteVec3('midWaterLight')};
    vec3 pebbleColor = ${paletteVec3('midWaterDark')};
    vec3 floorColor = mix(pebbleColor, base, pebbles) + vec3(sand);

    // Layer 3: caustic field — same 12s-cycle math as caustic.ts
    vec2 cuv = vUv * 4.0;
    vec2 warp = vec2(
      voronoi(cuv * 0.5 + uTime * 0.1, uTime),
      voronoi(cuv * 0.5 + 10.0 + uTime * 0.1, uTime)
    );
    cuv += warp * 0.3;
    float c = 1.0 - smoothstep(0.0, 0.5, voronoi(cuv, uTime));
    vec3 causticColor = mix(${paletteVec3('lightShaftGlow')}, ${paletteVec3('causticAccent')}, c);

    // Phase 6 T6.X — zone-graduated caustic attenuation. Matches Path B
    // pattern from waterSurface (Phase 5 T5.3): full caustic above the
    // safe-zone line, zero caustic below — so the floor doesn't render
    // L≈0.45+ peak pixels behind HeroContent's lower-zone text (subtitle,
    // CTAs, HeroTrustStrip). Above the safe zone (top 28% of viewport)
    // the look is preserved; below, the floor reads as the base
    // floorColor only (~rgb(40,30,20) L≈0.04 — text-friendly).
    float screenYNorm = gl_FragCoord.y / uResolution.y;
    float safeZoneEdgeNorm = ${SAFE_ZONE_COMPLEMENT};
    float fadeBandHalfWidth = 0.04;
    float zoneAttenuation = smoothstep(
      safeZoneEdgeNorm - fadeBandHalfWidth,
      safeZoneEdgeNorm + fadeBandHalfWidth,
      screenYNorm
    );
    // Composite: floor underneath, caustics blended on top at up to 50%
    // peak, gated by zone attenuation.
    vec3 finalColor = mix(floorColor, causticColor, c * 0.5 * zoneAttenuation);
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

export function makePondFloorMaterial(): {
  material: ShaderMaterial;
  uniforms: {
    uTime: { value: number };
    uResolution: { value: Vector2 };
  };
} {
  const initialW =
    typeof window !== 'undefined' ? window.innerWidth * (window.devicePixelRatio || 1) : 1;
  const initialH =
    typeof window !== 'undefined' ? window.innerHeight * (window.devicePixelRatio || 1) : 1;
  const uniforms = {
    uTime: { value: 0 },
    uResolution: { value: new Vector2(initialW, initialH) },
  };
  const material = new ShaderMaterial({
    vertexShader: POND_FLOOR_VERT,
    fragmentShader: POND_FLOOR_FRAG,
    uniforms,
  });
  return { material, uniforms };
}
